import React, {useState, FC} from "react";
import {useTranslation} from "next-i18next";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import certifications from "../../diver-certifications.json";
import InputLabel from "@mui/material/InputLabel";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "@mui/material/TextField";
import useWidth from "../../hooks/use-width";
import {IconButton} from "@mui/material";
import * as Yup from "yup";
import {Formik} from "formik";
import diverCertifications from '../../diver-certifications.json'
import {MutableDiverCertification} from "./certifications-card";
import {TransitionProps} from "@mui/material/transitions";

interface CertificationDetailsDialogProps {
    onEditFinished: (cert: MutableDiverCertification) => void
    cert: MutableDiverCertification
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export const CertificationDetailsDialog: FC<CertificationDetailsDialogProps> = ({cert, onEditFinished}) => {

    const {t} = useTranslation();

    const width = useWidth()

    const fullScreen = width == "xs"

    //
    // const handleChange = (event) => {
    //     event.persist();
    //     const val = event.target.value;
    //
    //     setCert((prevState) => {
    //         const newCert = {...prevState, [event.target.name]: val};
    //         if (event.target.name === "certBody") {
    //             newCert["certLevel"] = "";
    //         }
    //         return newCert;
    //     });
    // };

    // const handleSubmit = (event) => {
    //     if (!!cert.certBody && !!cert.certLevel) {
    //         onFinished(cert);
    //     }
    // };

    return (
        <Dialog
            fullScreen={fullScreen}
            open={!!cert}
            TransitionComponent={Transition}
            aria-labelledby="certification-dialog-title"
            PaperProps={{
                style: {margin: 4},
            }}
        >

            <Formik
                initialValues={
                    {
                        certBody: cert.certBody,
                        certLevel: cert.certLevel,
                        certLocation: cert.certLocation,
                    }
                }
                validationSchema={
                    Yup
                        .object()
                        .shape({
                            certBody: Yup
                                .string()
                                .required('Required'),
                            certLevel: Yup
                                .string()
                                .min(10)
                                .required('Required'),
                            certLocation: Yup
                                .string()
                                .min(3),
                        })
                }

                onSubmit={async (values, {
                    resetForm,
                    setErrors,
                    setStatus,
                    setSubmitting
                }): Promise<void> => {

                    setSubmitting(true);

                    try {

                        onEditFinished({
                            index: cert.index,
                            submitAction: "save",
                            certBody: values.certBody,
                            certLevel: values.certLevel,
                            certLocation: values.certLocation
                        })
                        // NOTE: Make API request

                    } catch (err) {
                        console.error(err);
                        //toast.error('Something went wrong!');
                        setStatus({success: false});
                        // @ts-ignore
                        setErrors({submit: err.message});
                        setSubmitting(false);
                    }
                }}
            >
                {({
                      errors,
                      handleBlur,
                      handleChange,
                      handleSubmit,
                      isSubmitting,
                      touched,
                      values
                  }): JSX.Element => (
                    <form onSubmit={handleSubmit}>
                        <DialogTitle id="certification-dialog-title">
                            {t("certification-details")}
                        </DialogTitle>

                        <DialogContent>


                            <InputLabel
                                shrink
                                htmlFor="certLevel-helper"
                                style={{marginTop: 6}}
                                // error={validationErrors.certLevel}
                            >
                                {t("certifying-agency-and-level")}
                            </InputLabel>
                            <Grid
                                container
                                direction={"row"}
                            >
                                <Grid item>
                                    <FormControl style={{marginRight: 6}}>
                                        <Select
                                            variant={"outlined"}
                                            value={values.certBody}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            inputProps={{
                                                name: "certBody",
                                                id: "certBody-helper",
                                            }}
                                            style={{minWidth: "6.5rem"}}
                                        >
                                            {Object.keys(certifications).map((keyName, idx) => {
                                                return (
                                                    <MenuItem key={keyName} value={keyName}>
                                                        {keyName}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item>
                                    <FormControl
                                        required={true}
                                        // error={validationErrors.certLevel}
                                    >
                                        <Select
                                            variant={"outlined"}
                                            disabled={!values.certBody}
                                            value={values.certLevel}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            inputProps={{
                                                name: "certLevel",
                                                id: "certLevel-helper",
                                            }}
                                            style={{minWidth: 300}}
                                            renderValue={(value) =>
                                                value
                                                    .replace(/Instructor$/, "Instr.")
                                                    .replace(/Advanced/, "Adv.")
                                            }
                                        >
                                            {!!values.certBody &&
                                                diverCertifications[values.certBody as keyof typeof diverCertifications].map((keyName: string, idx: number) => {
                                                    return (
                                                        <MenuItem key={keyName} value={keyName}>
                                                            {keyName}
                                                        </MenuItem>
                                                    );
                                                })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <TextField
                                margin={"normal"}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.certLocation}
                                id="certLocation"
                                name="certLocation"
                                label={t("place-certified")}
                                placeholder={t("place-certified-example")}
                                fullWidth
                            />
                        </DialogContent>
                        <DialogActions>
                            {cert.index != -1 &&
                                <div style={{flexGrow: 1}}>
                                    <IconButton
                                        aria-label="Delete"
                                        onClick={(e) => {
                                            onEditFinished({...cert, submitAction: "delete"});
                                        }}
                                    >
                                        <DeleteIcon color={"error"} fontSize="medium"/>
                                    </IconButton>
                                </div>
                            }
                            <Button
                                onClick={(e) => {
                                    onEditFinished({...cert, submitAction: "cancel"});
                                }}
                                color="primary"
                            >
                                {t("cancel")}
                            </Button>
                            <Button type="submit" variant={"contained"} color="primary">
                                {t("save")}
                            </Button>
                        </DialogActions>
                    </form>)}
            </Formik>
        </Dialog>
    );
}
